/**
 * Feedback enums - kept in lockstep with the backend Pydantic schema
 * in `backend/api/routes/feedback.py` + the SQLAlchemy enums in
 * `backend/shared/models/user_feedback.py`. Hand-maintained (no codegen).
 */

export type FeedbackType =
  | 'issue'
  | 'suggestion'
  | 'data_wrong'
  | 'confusing'
  | 'new_feature'
  | 'other'

export type FeedbackImpact =
  | 'blocking'
  | 'slowing'
  | 'minor'
  | 'idea'

export type FeedbackStatus =
  | 'new'
  | 'reviewing'
  | 'backlog'
  | 'in_progress'
  | 'resolved'
  | 'not_planned'

export const FEEDBACK_TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: 'issue', label: 'Issue' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'data_wrong', label: 'Data looks wrong' },
  { value: 'confusing', label: 'Confusing' },
  { value: 'new_feature', label: 'New feature' },
  { value: 'other', label: 'Other' },
]

export const FEEDBACK_IMPACT_OPTIONS: { value: FeedbackImpact; label: string }[] = [
  { value: 'blocking', label: 'Blocking' },
  { value: 'slowing', label: 'Slowing me down' },
  { value: 'minor', label: 'Minor' },
  { value: 'idea', label: 'Idea only' },
]

/**
 * Single source for each status's label + colour. Drives the admin
 * triage dropdown, the admin list chips/filter, and the submitter pills
 * in "My feedback" so no surface drifts on wording or colour.
 */
export const FEEDBACK_STATUS_META: Record<
  FeedbackStatus,
  { label: string; color: string }
> = {
  new: { label: 'New', color: '#007F98' }, // teal
  reviewing: { label: 'Reviewing', color: '#ED9B00' }, // amber
  backlog: { label: 'Added to backlog', color: '#6E59A5' }, // indigo
  in_progress: { label: 'In progress', color: '#1B73E8' }, // blue
  resolved: { label: 'Resolved', color: '#2E7D32' }, // green
  not_planned: { label: 'Not planned', color: '#6B7280' }, // grey
}

export const FEEDBACK_STATUS_OPTIONS: { value: FeedbackStatus; label: string }[] = (
  Object.keys(FEEDBACK_STATUS_META) as FeedbackStatus[]
).map(value => ({ value, label: FEEDBACK_STATUS_META[value].label }))

export interface FeedbackContext {
  fp_code?: string | null
  fp_name?: string | null
  country?: string | null
  route: {
    pathname: string
    /** Raw query string (incl. leading "?"). Empty when none. */
    search?: string
    params: Record<string, string>
    filters: Record<string, string>
    /** Previous in-app route (pathname + query); null on direct load. */
    referrer?: string | null
  }
  browser: {
    family: string
    os: string
    viewport_w: number
    viewport_h: number
    language: string
  }
  app: {
    version: string
    sha: string
    env: 'dev' | 'staging' | 'prod'
  }
  timestamp: string
  timezone: string
  correlation_id?: string | null
  /**
   * External referrer (document.referrer) - the page outside the SPA
   * that loaded the app. Often null for in-app navigation.
   */
  entry_referrer?: string | null
  /**
   * Server-set: not provided by the frontend helper. Stored in the
   * context JSON for debugging and shown in the admin detail dialog.
   */
  client_ip?: string | null
}

export interface FeedbackRow {
  id: number
  type: FeedbackType
  impact: FeedbackImpact
  title: string | null
  details: string
  trying_to_do: string | null
  follow_up_ok: boolean
  status: FeedbackStatus
  /** Submitter-visible team reply (internal notes never reach this type). */
  update_note: string | null
  update_note_updated_at: string | null
  fp_code: string | null
  fp_name: string | null
  created_at: string
  updated_at: string | null
}

export interface AdminFeedbackSummary {
  id: number
  type: FeedbackType
  impact: FeedbackImpact
  title: string | null
  status: FeedbackStatus
  fp_code: string | null
  fp_name: string | null
  country: string | null
  user_id: number
  user_email: string | null
  user_name: string | null
  created_at: string
  updated_at: string | null
}

export interface AdminFeedbackDetail extends AdminFeedbackSummary {
  details: string
  trying_to_do: string | null
  follow_up_ok: boolean
  update_note: string | null
  internal_note: string | null
  update_note_updated_at: string | null
  source_pathname: string | null
  app_sha: string | null
  browser_family: string | null
  context: FeedbackContext | null
}

export interface AdminFeedbackCounts {
  new_total: number
  new_today: number
}
